import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Play, MessageCircle, X } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import { useContent } from "../contexts/ContentContext";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import { ChinaMap } from "../components/ChinaMap";
import { ContactForm } from "../components/ContactForm";
import { resolveImage } from "../lib/image";

const FALLBACK_SLIDE = "https://images.unsplash.com/photo-1626487129383-e0a379fa957b?crop=entropy&cs=srgb&fm=jpg&w=1920&q=85";
const FALLBACK_POPULAR = "https://images.unsplash.com/photo-1474181487882-5abf3f0ba6c2?auto=format&fit=crop&w=900&q=80";

const TEMPLE_IMG =
  "https://images.unsplash.com/photo-1547981609-4b6bfe67ca0b?auto=format&fit=crop&w=900&q=80";

const getYoutubeEmbedUrl = (url) => {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace("www.", "");
    if (host === "youtu.be") {
      const id = parsed.pathname.slice(1);
      return id ? `https://www.youtube.com/embed/${id}` : null;
    }
    if (host === "youtube.com" || host === "m.youtube.com") {
      if (parsed.pathname === "/watch") {
        const id = parsed.searchParams.get("v");
        return id ? `https://www.youtube.com/embed/${id}` : null;
      }
      if (parsed.pathname.startsWith("/embed/")) {
        return `https://www.youtube.com${parsed.pathname}`;
      }
      if (parsed.pathname.startsWith("/shorts/")) {
        const id = parsed.pathname.split("/")[2];
        return id ? `https://www.youtube.com/embed/${id}` : null;
      }
    }
  } catch {
    return null;
  }
  return null;
};

export default function Home() {
  const { t } = useI18n();
  const { content } = useContent();
  const visibility = content?.visibility || {};
  const channels = content?.channels || {};
  const heroRef = useRef(null);
  const [active, setActive] = useState(0);
  const [videoOpen, setVideoOpen] = useState(false);
  const slides = (t.home.heroSlides || []);
  const inspireVideoUrl = t.home.inspireVideoUrl || "";
  const inspireYoutubeEmbed = getYoutubeEmbedUrl(inspireVideoUrl);
  const inspireVideoSrc = inspireYoutubeEmbed ? null : resolveImage(inspireVideoUrl);

  // Auto-advance slides
  useEffect(() => {
    if (!slides.length) return;
    const id = setInterval(() => {
      setActive((p) => (p + 1) % slides.length);
    }, 6000);
    return () => clearInterval(id);
  }, [slides.length]);

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const yBg = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const opacityBg = useTransform(scrollYProgress, [0, 1], [1, 0.5]);

  return (
    <>
      {/* HERO with city slider */}
      <section
        ref={heroRef}
        data-testid="home-hero"
        className="relative min-h-[100vh] overflow-hidden bg-midnight text-white"
      >
        {/* Slide images */}
        <motion.div
          style={{ y: yBg, opacity: opacityBg }}
          className="absolute inset-0"
        >
          <AnimatePresence>
            <motion.div
              key={active}
              initial={{ opacity: 0, scale: 1.06 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 1 }}
              transition={{ duration: 1.2, ease: "easeOut" }}
              className="absolute inset-0"
            >
              <img
                src={resolveImage(slides[active]?.image) || FALLBACK_SLIDE}
                alt={slides[active]?.city || ""}
                className="w-full h-full object-cover"
              />
            </motion.div>
          </AnimatePresence>
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(to right, rgb(var(--c-midnight) / var(--hero-ol-left)), rgb(var(--c-midnight) / var(--hero-ol-mid)), rgb(var(--c-midnight) / var(--hero-ol-right)))",
            }}
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(to top, rgb(var(--c-midnight) / var(--hero-ol-bottom)), rgb(var(--c-midnight) / var(--hero-ol-center)), rgb(var(--c-midnight) / 0))",
            }}
          />
        </motion.div>

        <div className="absolute inset-0 tech-grid-bg opacity-40" />
        <div className="noise-overlay" />

        <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 pt-32 md:pt-40 pb-12 min-h-[100vh] flex flex-col">
          {/* Top spacer */}
          <div className="flex-1 flex items-center">
            <div className="w-full max-w-5xl">
              <AnimatePresence mode="wait">
                <motion.div
                  key={active}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                >
                  <h1
                    className="font-display font-black tracking-tighter leading-[0.88] text-white text-6xl sm:text-7xl md:text-8xl lg:text-[140px] uppercase"
                    data-testid="hero-title"
                  >
                    {slides[active].title.split(" ")[0]}
                    <br />
                    <span className="text-white">{slides[active].title.split(" ")[1]}</span>
                  </h1>
                  <p className="mt-8 max-w-xl text-white/70 text-lg md:text-xl font-display leading-relaxed">
                    {slides[active].desc}
                  </p>
                </motion.div>
              </AnimatePresence>

              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.5 }}
                className="mt-12 flex flex-col sm:flex-row gap-4"
              >
                <Link
                  to="/contacts"
                  data-testid="hero-cta-program"
                  className="btn-primary !py-5 !px-8"
                >
                  {t.nav.cta}
                </Link>
              <a
                href={channels.telegram_url || "https://t.me/diyatour"}
                data-testid="hero-cta-telegram"
                className="btn-ghost !py-5 !px-8"
              >
                <MessageCircle size={14} /> Telegram
              </a>
              </motion.div>
            </div>
          </div>

          {/* Pillars row at the bottom of hero */}
          <div className="border-t border-white/10 pt-10">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-16">
              {t.home.pillars.map((p, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: 0.6 + i * 0.1 }}
                >
                  <div className="font-mono text-[11px] tracking-[0.25em] uppercase text-coral mb-3">
                    /0{i + 1} · {p.t}
                  </div>
                  <p className="text-white/65 text-sm leading-relaxed mb-5 max-w-xs">
                    {p.d}
                  </p>
                  <Link
                    to={["/business", "/business", "/retreat"][i]}
                    className="link-more"
                    data-testid={`hero-pillar-${i}`}
                  >
                    {t.home.learnMore} <ArrowRight size={14} />
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* Right vertical pagination */}
        <div className="hidden lg:flex absolute right-12 top-1/2 -translate-y-1/2 flex-col items-end gap-3 z-10">
          {slides.map((s, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              data-testid={`hero-pagination-${i}`}
              className="flex items-center gap-4 group"
            >
              <span
                className={`slide-num ${i === active ? "active" : ""}`}
              >
                {String(i + 1).padStart(2, "0")}
              </span>
              <span
                className={`block h-px bg-coral transition-all duration-500 ${
                  i === active ? "w-16 opacity-100" : "w-6 opacity-30 group-hover:opacity-60"
                }`}
              />
            </button>
          ))}
        </div>
      </section>

      {/* POPULAR TOURS */}
      {visibility.home_popular !== false && (
      <section className="relative bg-midnight" data-testid="popular-section">
        <div className="absolute inset-0 tech-grid-bg opacity-30" />
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto">
              <div className="text-white/55 text-sm md:text-base italic font-display mb-3">
                {t.home.popularOverline}
              </div>
              <h2 className="font-display font-black tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl uppercase">
                {t.home.popularTitle}
              </h2>
            </div>
          </Reveal>

          <Stagger className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-5">
            {t.home.popularItems.map((item, idx) => (
              <StaggerItem key={idx}>
                <Link
                  to="/contacts"
                  data-testid={`popular-tour-${idx}`}
                  className="group block"
                >
                  <div className="aspect-[4/5] overflow-hidden bg-midnight-2 relative">
                    <img
                      src={resolveImage(item.image) || FALLBACK_POPULAR}
                      alt={item.t}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-midnight/80 via-transparent to-transparent" />
                    <div className="absolute top-4 left-4 font-mono text-[10px] tracking-widest uppercase text-coral bg-midnight/60 backdrop-blur px-3 py-1.5">
                      0{idx + 1}
                    </div>
                  </div>
                  <div className="mt-5 text-center">
                    <div className="font-display font-extrabold tracking-tight text-white text-xl md:text-2xl uppercase group-hover:text-coral transition-colors">
                      {item.tag}
                    </div>
                    <div className="mt-1.5 font-display text-white/55 text-sm">{item.t}</div>
                    <div className="font-mono text-[11px] tracking-widest uppercase text-white/35 mt-2">
                      {item.d}
                    </div>
                  </div>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>

          <div className="mt-14 flex justify-center">
            <Link to="/business" className="link-more" data-testid="popular-all">
              {t.home.learnMore} <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
      )}

      {/* TRAVEL AND INSPIRE — video section */}
      {visibility.home_inspire !== false && (
      <section
        className="relative overflow-hidden bg-gradient-to-b from-midnight via-[#1a2240] to-[#2a1f4a]"
        data-testid="inspire-section"
      >
        <div className="stars" />
        <div className="absolute inset-0 bg-gradient-to-r from-midnight/80 via-transparent to-transparent" />
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 pt-20 md:pt-36 pb-16 md:pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          <Reveal className="lg:col-span-6 lg:pr-8 min-w-0">
            <div className="overline mb-5">{t.home.inspireOverline}</div>
            <h2 className="font-display font-black tracking-normal md:tracking-tighter text-white text-[42px] sm:text-6xl lg:text-7xl uppercase leading-[0.94] md:leading-[0.92] max-w-full lg:max-w-[10ch] break-words">
              {t.home.inspireTitle.split("\n").map((line, i) => (
                <span key={i} className="block">
                  {line}
                </span>
              ))}
            </h2>
            <p className="mt-8 max-w-lg text-white/70 text-base md:text-lg font-display leading-relaxed">
              {t.home.inspireDesc}
            </p>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-6">
            <div className="relative">
              <div className="aspect-video sm:aspect-[4/3] overflow-hidden bg-midnight-2 relative group cursor-pointer">
                <img
                  src={TEMPLE_IMG}
                  alt=""
                  className="w-full h-full object-cover opacity-80 group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <button
                    type="button"
                    onClick={() => setVideoOpen(true)}
                    className="w-12 h-12 rounded-full bg-white/95 flex items-center justify-center group-hover:bg-coral transition-colors"
                  >
                    <Play size={14} className="text-midnight ml-0.5 fill-midnight" />
                  </button>
                </div>
              </div>
            </div>
          </Reveal>
        </div>

      </section>
      )}
      <AnimatePresence>
        {videoOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm p-4 md:p-10"
            onClick={() => setVideoOpen(false)}
          >
            <div className="mx-auto h-full max-w-5xl flex items-center">
              <div
                className="relative w-full bg-black border border-white/10"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  onClick={() => setVideoOpen(false)}
                  className="absolute -top-11 right-0 text-white/80 hover:text-white p-2"
                  aria-label="Close video"
                >
                  <X size={22} />
                </button>
                <div className="aspect-video bg-black">
                  {inspireYoutubeEmbed ? (
                    <iframe
                      src={inspireYoutubeEmbed}
                      title="Inspire video"
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                    />
                  ) : inspireVideoSrc ? (
                    <video className="w-full h-full" controls autoPlay>
                      <source src={inspireVideoSrc} />
                    </video>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white/70 font-display">
                      Видео не задано в админке
                    </div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* BENCHMARK */}
      {visibility.home_benchmark !== false && (
      <section className="relative bg-midnight" data-testid="benchmark-section">
        <div className="absolute inset-0 tech-grid-bg opacity-40" />
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32">
          <Reveal>
            <div className="overline mb-6">{t.home.benchmarkOverline}</div>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display font-extrabold tracking-tighter text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.98] max-w-4xl uppercase">
              {t.home.benchmarkTitle}
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 max-w-2xl text-white/65 text-base md:text-lg leading-relaxed">
              {t.home.benchmarkDesc}
            </p>
          </Reveal>

          <Stagger className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5">
            {t.home.companies.map((c, idx) => (
              <StaggerItem
                key={c.name}
                className="group relative bg-midnight-2 p-8 lg:p-10 transition-all duration-500 hover:bg-coral hover:text-midnight cursor-pointer overflow-hidden"
              >
                <div className="flex items-start justify-between mb-12">
                  <span className="font-mono text-[11px] tracking-[0.2em] text-coral group-hover:text-midnight uppercase">
                    {c.tag}
                  </span>
                  <span className="font-mono text-[11px] tracking-widest text-white/30 group-hover:text-midnight/50">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="font-display font-extrabold tracking-tight text-2xl md:text-3xl mb-3 leading-tight">
                  {c.name}
                </h3>
                <p className="text-white/55 group-hover:text-midnight/80 text-sm leading-relaxed transition-colors">
                  {c.desc}
                </p>
                <div className="mt-12 flex items-center gap-3 text-white/40 group-hover:text-midnight transition-colors">
                  <span className="line-arrow" />
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>
      )}

      {/* WHY US */}
      {visibility.home_why !== false && (
      <section className="relative bg-[#0E1117]" data-testid="why-section">
        <div className="absolute inset-0 tech-grid-bg opacity-30" />
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32">
          <Reveal>
            <div className="overline mb-6">{t.home.whyOverline}</div>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display font-extrabold tracking-tighter text-white text-3xl sm:text-4xl lg:text-5xl leading-[1.05] max-w-3xl uppercase">
              {t.home.whyTitle}
            </h2>
          </Reveal>

          <Stagger className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5">
            {t.home.whyItems.map((item, idx) => (
              <StaggerItem
                key={idx}
                className="bg-[#0E1117] p-8 group hover:bg-midnight-2 transition-colors"
              >
                <div className="flex items-start gap-4">
                  <span className="font-mono text-[11px] tracking-widest text-coral mt-1.5">
                    /{String(idx + 1).padStart(2, "0")}
                  </span>
                  <p className="font-display font-semibold text-white text-lg leading-snug">
                    {item}
                  </p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>
      )}

      {/* MAP + EXPERIENCE */}
      {visibility.home_map !== false && (
      <section className="bg-midnight border-t border-white/5" data-testid="map-section">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-4">
            <div className="overline mb-6">{t.home.experienceOverline}</div>
            <h2 className="font-display font-extrabold tracking-tighter text-white text-3xl sm:text-4xl lg:text-5xl leading-[1.05] uppercase">
              {t.home.experienceTitle}
            </h2>
            <div className="mt-12 space-y-6">
              {t.home.experienceItems.map((it, idx) => (
                <div
                  key={idx}
                  className="grid grid-cols-[120px_1fr] items-start gap-6 border-b border-white/10 pb-6"
                >
                  <div className="font-display font-black text-white text-5xl md:text-6xl leading-none">
                    {it.num}
                  </div>
                  <div className="text-white/65 text-sm md:text-base leading-relaxed pt-1">
                    {it.label}
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-8 lg:h-full">
            <div className="h-[420px] sm:h-[500px] lg:h-full lg:min-h-[640px] bg-midnight-2 relative overflow-hidden border border-white/5">
              <ChinaMap className="w-full h-full" mapSettings={t.home.mapSettings} />
            </div>
          </Reveal>
        </div>
      </section>
      )}

      {/* CTA + FORM */}
      {visibility.home_cta !== false && (
      <section
        className="relative bg-midnight-2 text-white overflow-hidden border-t border-white/5"
        data-testid="home-cta"
      >
        <div className="absolute inset-0 tech-grid-bg opacity-50" />
        <div className="absolute -top-32 -right-32 w-[600px] h-[600px] rounded-full bg-coral/20 blur-3xl" />
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
          <Reveal className="md:col-span-6">
            <div className="overline mb-6">CTA · 联系我们</div>
            <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[0.95] uppercase">
              {t.home.ctaTitle}
            </h2>
            <p className="mt-8 text-white/65 text-lg max-w-lg leading-relaxed">
              {t.home.ctaDesc}
            </p>
            <div className="mt-12 flex flex-wrap gap-3">
              <a
                href="https://t.me/diyatour"
                className="inline-flex items-center gap-2 border border-white/20 px-5 py-3 font-mono text-[12px] tracking-widest uppercase hover:border-coral hover:text-coral transition-colors"
              >
                <MessageCircle size={14} /> Telegram
              </a>
              <a
                href="https://wa.me/0000000000"
                className="inline-flex items-center gap-2 border border-white/20 px-5 py-3 font-mono text-[12px] tracking-widest uppercase hover:border-coral hover:text-coral transition-colors"
              >
                <MessageCircle size={14} /> WhatsApp
              </a>
              <span className="inline-flex items-center gap-2 border border-white/20 px-5 py-3 font-mono text-[12px] tracking-widest uppercase">
                <MessageCircle size={14} /> WeChat
              </span>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-6">
            <ContactForm />
          </Reveal>
        </div>
      </section>
      )}
    </>
  );
}
